import React, { useEffect, useState } from "react";
import { Form, Input, Select, Switch, message } from "antd";
import FormModal from "../../components/FormModal";
import PasswordFields from "../../components/PasswordFields";
import { extractMsg } from "../../utils/errorUtils";

export function AddUserDialog({ open, onClose, onSave, rolesList }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) form.resetFields();
  }, [open, form]);

  const handleFinish = async (values) => {
    setLoading(true);
    try {
      await onSave(values);
      form.resetFields();
      onClose();
    } catch (error) {
      message.error(extractMsg(error, "Failed to create user"));
      console.error("Failed to create user", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      title="Add New Fahras User"
      open={open}
      onClose={() => {
        form.resetFields();
        onClose();
      }}
      onFinish={handleFinish}
      loading={loading}
      form={form}
      submitText="Create User"
      width={600}
      initialValues={{ isAdmin: false, roles: [] }}
    >
      <Form.Item
        name="name"
        label="Username"
        rules={[{ required: true, message: "Username is required" }]}
      >
        <Input placeholder="Enter username (e.g., jdoe)" />
      </Form.Item>
      <Form.Item
        name="displayName"
        label="Display Name"
        rules={[{ required: true, message: "Display name is required" }]}
      >
        <Input placeholder="Enter display name (e.g., John Doe)" />
      </Form.Item>
      <Form.Item
        name="email"
        label="Email Address"
        rules={[
          { required: true, message: "Email is required" },
          { type: "email", message: "Please enter a valid email" },
        ]}
      >
        <Input placeholder="Enter email address" />
      </Form.Item>
      <PasswordFields />
      <Form.Item
        name="roles"
        label="Roles"
        rules={[{ required: true, message: "Please select at least one role" }]}
      >
        <Select
          mode="multiple"
          allowClear
          placeholder="Select user roles"
          options={rolesList.map((r) => ({
            label: r.displayName || r.name,
            value: r.id,
          }))}
        />
      </Form.Item>
      <Form.Item
        name="isAdmin"
        label="Admin Privileges"
        valuePropName="checked"
        tooltip="Grants administrator capabilities to this user."
      >
        <Switch checkedChildren="Yes" unCheckedChildren="No" />
      </Form.Item>
    </FormModal>
  );
}

export function EditUserDialog({ open, user, onClose, onSave }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && user) {
      form.setFieldsValue({
        name: user.name,
        displayName: user.displayName || user.name,
        email: user.email,
        isAdmin: user.isAdmin,
        password: "",
        confirmPassword: "",
      });
    }
  }, [open, user, form]);

  const handleFinish = async (values) => {
    setLoading(true);
    try {
      await onSave(values);
      onClose();
    } catch (error) {
      console.error("Failed to edit user", error);
      message.error(extractMsg(error, "Failed to edit user"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      title="Edit Fahras User"
      open={open}
      onClose={onClose}
      onFinish={handleFinish}
      loading={loading}
      form={form}
      submitText="Save Changes"
      width={600}
    >
      <Form.Item
        name="name"
        label="Username"
        rules={[{ required: true, message: "Username is required" }]}
      >
        <Input disabled />
      </Form.Item>
      <Form.Item
        name="displayName"
        label="Display Name"
        rules={[{ required: true, message: "Display name is required" }]}
      >
        <Input placeholder="Enter display name" />
      </Form.Item>
      <Form.Item
        name="email"
        label="Email Address"
        rules={[
          { required: true, message: "Email is required" },
          { type: "email", message: "Please enter a valid email" },
        ]}
      >
        <Input placeholder="Enter email address" />
      </Form.Item>
      <PasswordFields required={false} />
      <Form.Item
        name="isAdmin"
        label="Admin Privileges"
        valuePropName="checked"
        tooltip="Grants administrator capabilities to this user."
      >
        <Switch checkedChildren="Yes" unCheckedChildren="No" />
      </Form.Item>
    </FormModal>
  );
}
